import { t } from '@lingui/macro';
import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';

import {
  getIdentities,
  PeopleTaggingStatus,
  type AttachmentFaceResponse,
  type IdentityResponse
} from '@api/v4/private/people_tagging/faces';
import { usePeopleTaggingFaces } from '@components/asset/modal/tabs/shared/hooks/usePeopleTaggingFaces';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { Person } from '@components/people_tags/customPeopleTagTypes';
import { TextSanitizationTooltip } from '@components/shared/sanitization';

import assetModalContext from '../asset_modal_context';
import { ActionTypes, PeopleTaggingChangeType, type PeopleTaggingChange } from '../EditTabTypes';

import { PeopleTagEditField, type FaceData } from './PeopleTagEditField';
import { SameNameDetectedModal } from './SameNameDetectedModal';
import Wrapper, {
  PeopleSection,
  SectionHeader,
  HeaderText,
  PeopleContent,
  PeopleTagsContainer
} from './styles/PeopleInAssetsComponents';

function PeopleInAsset(): React.ReactElement {
  const { state, dispatch } = useContext(assetModalContext);

  // Get attachment key and brandfolder key
  const attachmentKey = state?.editState?.firstAttachmentKey || '';
  const brandfolderKey = BFG?.resource?.key || BFG?.brandfolder_key || '';

  // State to manage identities
  const [identities, setIdentities] = useState<IdentityResponse[]>([]);
  const [isTabActive, setIsTabActive] = useState<boolean>(false);

  // Check if edit tab is active/visible
  useEffect(() => {
    const checkTabActive = (): void => {
      const editTabPane = document.getElementById('edit');
      const isActive = editTabPane?.classList.contains('active') ?? false;
      setIsTabActive(isActive);
    };

    // Check initially
    checkTabActive();

    const editTabPane = document.getElementById('edit');
    if (!editTabPane) return;

    // Use MutationObserver to watch for class changes on the tab pane
    const observer = new MutationObserver(() => {
      checkTabActive();
    });

    // Observe changes to the 'class' attribute
    observer.observe(editTabPane, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Also listen for Bootstrap tab show event (jQuery event)
    const handleTabShown = (): void => {
      setTimeout(checkTabActive, 0);
    };

    // Listen on the tab link element if it exists
    const editTabLink = document.querySelector('a[href="#edit"]');
    type JQueryElement = {
      on: (event: string, handler: () => void) => void;
      off: (event: string, handler: () => void) => void;
    };
    let jqueryElement: JQueryElement | null = null;

    if (editTabLink) {
      const jquery = (window as unknown as { $?: unknown }).$;
      if (jquery && typeof jquery === 'function') {
        jqueryElement = (jquery as (selector: Element) => JQueryElement)(editTabLink);
        jqueryElement.on('shown.bs.tab', handleTabShown);
      } else {
        editTabLink.addEventListener('click', handleTabShown);
      }
    }

    // Also listen on document as fallback
    document.addEventListener('shown.bs.tab', handleTabShown);

    return (): void => {
      observer.disconnect();
      if (editTabLink) {
        if (jqueryElement) {
          jqueryElement.off('shown.bs.tab', handleTabShown);
        } else {
          editTabLink.removeEventListener('click', handleTabShown);
        }
      }
      document.removeEventListener('shown.bs.tab', handleTabShown);
    };
  }, []);

  // Use shared hook for faces polling
  const { faces: facesResponse, isLoading: isFacesLoading, status } = usePeopleTaggingFaces({
    attachmentKey,
    brandfolderKey,
    enabled: isTabActive
  });

  // Transform faces response to FaceData format
  const facesFromHook: FaceData[] = useMemo(() => {
    return facesResponse.map((face: AttachmentFaceResponse) => ({
      identityId: face.identity_id,
      faceId: face.id,
      name: face.identity_name || '',
      assetUrl: face.source_url,
      faceThumbnailUrl: face.face_thumbnail_url,
      isHidden: JSON.parse(face.identity_metadata || '{}')?.hide || false,
    }));
  }, [facesResponse]);

  // Local state for optimistic UI updates
  const [optimisticFaces, setOptimisticFaces] = useState<FaceData[]>(facesFromHook);

  // Sync local state with hook data (when hook updates, reset optimistic state)
  useEffect(() => {
    setOptimisticFaces(facesFromHook);
  }, [facesFromHook]);

  // Use optimistic faces for UI (allows for optimistic updates)
  const faces = optimisticFaces;

  // State for same name modal
  const [showSameNameModal, setShowSameNameModal] = useState(false);
  const [pendingPersonName, setPendingPersonName] = useState('');
  const [pendingIdentityId, setPendingIdentityId] = useState<string | null>(null);

  // Fetch identities when faces are loaded (not waiting for processing)
  useEffect(() => {
    if (!brandfolderKey || !isTabActive) {
      setIdentities([]);
      return;
    }

    // Only fetch identities when faces are ready (not waiting for processing)
    if (isFacesLoading || status === PeopleTaggingStatus.WaitingForPeopleTagging) {
      return;
    }

    // Fetch identities when faces are available
    const loadIdentities = async (): Promise<void> => {
      try {
        const identitiesResponse = await getIdentities({ brandfolderKey });
        setIdentities(identitiesResponse);
      } catch (error) {
        console.error(t`Error loading identities:`, error);
        setIdentities([]);
      }
    };

    void loadIdentities();
  }, [brandfolderKey, isTabActive, isFacesLoading, status]);

  // Handle removing a face (deleting it)
  const handleRemovePerson = useCallback((face: FaceData): void => {
    if (!dispatch) return;

    // Update local UI state optimistically
    setOptimisticFaces((prev) => prev.filter((f) => f.identityId !== face.identityId));

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      identityId: face.identityId,
      faceId: face.faceId || '',
      name: face.name || '',
      type: PeopleTaggingChangeType.DeleteFace,
      attachmentKey
    };

    // Remove any existing change for this faceId and add new one
    const filteredChanges = currentChanges.filter((change) => change.identityId !== face.identityId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });
  }, [dispatch, state?.editState?.peopleTaggingChanges, attachmentKey]);

  // Handle identifying a person from dropdown or creating new one
  const onMergeIdentities = useCallback((sourceIdentityId: string, destinationIdentity: Person): void => {
    if (!dispatch) return;

    const destinationIdentityId = destinationIdentity.person_id;

    // Update local UI state optimistically
    setOptimisticFaces((prev) =>
      prev.map((f) =>
        f.identityId === sourceIdentityId ? { ...f, name: destinationIdentity.tag_name } : f
      )
    );

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      destinationId: destinationIdentityId,
      destinationName: destinationIdentity.tag_name,
      sourceId: sourceIdentityId,
      sourceName: '',
      type: PeopleTaggingChangeType.MergeIdentity,
    };

    // Remove any existing change for this identityId and add new one
    const filteredChanges = currentChanges.filter((change) => change.identityId !== sourceIdentityId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });
  }, [dispatch, state?.editState?.peopleTaggingChanges]);

  const handleUpdateIdentityName = useCallback((identityId: string, name: string): void => {
    if (!dispatch) return;

    const trimmedName = name.trim();

    // Update local UI state optimistically
    setOptimisticFaces((prev) =>
      prev.map((f) =>
        f.identityId === identityId ? { ...f, name: trimmedName } : f
      )
    );

    // Add to pending changes for save
    const currentChanges = state?.editState?.peopleTaggingChanges || [];
    const newChange: PeopleTaggingChange = {
      identityId,
      name: trimmedName,
      type: PeopleTaggingChangeType.UpdateIdentity,
    };

    // Remove any existing change for this identityId and add new one
    const filteredChanges = currentChanges.filter((change) => change.identityId !== identityId);
    dispatch({
      type: ActionTypes.UpdateAsset,
      payload: { peopleTaggingChanges: [...filteredChanges, newChange] }
    });

    setShowSameNameModal(false);
    setPendingPersonName('');
    setPendingIdentityId(null);
  }, [
    dispatch,
    state?.editState?.peopleTaggingChanges,
    setShowSameNameModal,
    setPendingPersonName,
    setPendingIdentityId,
  ]);

  // Handle creating a new person with the entered name
  const onUpdateIdentityName = useCallback((identityId: string, name: string): void => {
    if (!name.trim()) return;

    // Check if there's an existing identity with the same name
    const existingIdentityHaveSameName = identities.find((identity) =>
      identity.name.toLowerCase() === name.toLowerCase()
    );

    if (existingIdentityHaveSameName) {
      // Show same name modal
      setPendingPersonName(name.trim());
      setPendingIdentityId(identityId);
      setShowSameNameModal(true);
    } else {
      // Create a new person with the entered name
      handleUpdateIdentityName(identityId, name.trim());
    }
  }, [identities, handleUpdateIdentityName]);

  // Handle canceling the same name modal - just close it without taking any action
  const onCancelSameNameModal = useCallback((): void => {
    // Just close the modal and reset state
    setShowSameNameModal(false);
    setPendingPersonName('');
    setPendingIdentityId(null);
  }, []);

  const showLoader = isFacesLoading || status === PeopleTaggingStatus.WaitingForPeopleTagging;

  if (showLoader) {
    return (
      <I18nProviderWrapper>
        <Wrapper>
          <PeopleSection>
            <SectionHeader>
              <HeaderText>{t`People in asset`}</HeaderText>
            </SectionHeader>
            <PeopleContent>
              <div style={{ padding: '20px', textAlign: 'center', width: '100%' }}>
                <span className="bff-spinner-gif" />
              </div>
            </PeopleContent>
          </PeopleSection>
        </Wrapper>
      </I18nProviderWrapper>
    );
  }

  const onlyVisibleFaces = faces.filter((face) => !face.isHidden);

  if (onlyVisibleFaces.length === 0) {
    return null;
  }

  return (
    <I18nProviderWrapper>
      <Wrapper>
        <PeopleSection>
          <SectionHeader>
            <HeaderText>{t`People in asset`}</HeaderText>
            <TextSanitizationTooltip
              margin
              tooltipText={t`
                Name the faces in this asset by selecting from existing people or typing a new name.
                If a face was misidentified, remove the name and enter the correct one —
                this change only affects the current asset.
              `}
            />
          </SectionHeader>
          <PeopleContent>
            <PeopleTagsContainer>
              {faces.map((face) => {
                if (face.isHidden) {
                  return null;
                }

                return (
                <PeopleTagEditField
                  key={face.identityId}
                  face={face}
                  identities={identities}
                  onMergeIdentities={onMergeIdentities}
                  onRemoveFaceName={handleRemovePerson}
                  onUpdateIdentityName={onUpdateIdentityName}
                />
                );
              })}
          </PeopleTagsContainer>
          </PeopleContent>
        </PeopleSection>
      </Wrapper>
      {/* Same Name Detected Modal */}
      <SameNameDetectedModal
        isOpen={showSameNameModal}
        onCancel={onCancelSameNameModal}
        onCreateNew={(): void => {
          void handleUpdateIdentityName(pendingIdentityId, pendingPersonName);
        }}
      />
    </I18nProviderWrapper>
  );
}

export default PeopleInAsset;
